// Explicit-vs-default status for TOP-LEVEL (non-style) object properties.
//
// Style properties have a real sparse store and a resolver, so "explicit" is a
// stored fact there (node.hasExplicitStyle(), StyleResolver.hasExplicitValue).
// Top-level properties are different by design: ConfigManager copies them onto
// the node as plain instance fields (see ConfigManager.js), pre-filled from
// theme → schema defaults, so there is no "absent" state to read back.
//
// The model's own notion of "not set" for these is therefore: *the value still
// equals its effective default* (theme slice first, then schema
// defaults.properties, i.e. node.defaults) — which is exactly the rule
// ObjectExport uses to decide whether a property is emitted at all
// (`notDefault`). This module owns that rule so the Property Editor's
// indicator and the exporter can never drift apart, and so no UI file has to
// hardcode a default value.
//
// Consequently `reset` here means "write the effective default back", after
// which the property is indistinguishable from never-having-been-set: the
// exporter omits it again and the theme/schema default governs. Properties
// whose emission is not default-driven (geometry, identity, designer-only
// flags — OBJECT_PROP_SKIP) have no such state and are reported as not
// resettable rather than given fake reset behaviour.
import { OBJECT_PROP_SKIP, isHaspProperty, getObjectSchema } from './v2/wireFormat.js';

/** The node field a top-level property is stored in ('hasp_'-prefixed for `hasp: true` metadata). */
export function objectPropertyField(node, prop) {
    const schema = getObjectSchema(node?.obj);
    return isHaspProperty(schema, prop) ? `hasp_${prop}` : prop;
}

/** Current value of a top-level property. A Konva prototype method means the plain property was never set. */
export function objectPropertyValue(node, prop) {
    const value = node?.[objectPropertyField(node, prop)];
    return typeof value === 'function' ? undefined : value;
}

/**
 * The value this property falls back to when it carries no user change:
 * the active theme's slice for this widget type first, then the schema
 * default (node.defaults). undefined when the property has no default at all.
 */
export function effectiveObjectPropertyDefault(node, prop) {
    const themeValue = node?._activeThemeSlice?.()?.properties?.[prop];
    return themeValue !== undefined ? themeValue : node?.defaults?.[prop];
}

/**
 * True when this property has a default to fall back to AND its emission is
 * default-driven. Geometry (x/y/width/height), identity (haspid/parentid) and
 * designer-only flags are always written explicitly by the exporter, so they
 * have no "using the default" state to show or restore.
 */
export function isObjectPropertyResettable(node, prop) {
    if (!node || OBJECT_PROP_SKIP.has(prop)) return false;
    return effectiveObjectPropertyDefault(node, prop) !== undefined;
}

/** True when the current value differs from the effective default — the same test ObjectExport's `notDefault` makes. */
export function isObjectPropertyModified(node, prop) {
    if (!isObjectPropertyResettable(node, prop)) return false;
    const value = objectPropertyValue(node, prop);
    return value !== undefined && value !== effectiveObjectPropertyDefault(node, prop);
}

/**
 * Drop the user's change by restoring the effective default (the only
 * representation of "unset" these flat properties have). Returns the value
 * written, or undefined when the property is not resettable — the caller still
 * owns notifying the node (node.adjust(...)), exactly as its editor does.
 */
export function resetObjectProperty(node, prop) {
    if (!isObjectPropertyResettable(node, prop)) return undefined;
    const value = effectiveObjectPropertyDefault(node, prop);
    node[objectPropertyField(node, prop)] = value;
    return value;
}
